import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL || "";

  // Global axios 401 interceptor — only clears user state, doesn't force redirect
  // (the route guard handles redirect to /login).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const isAuthCheck = error.config?.url?.includes('/api/auth/me');
        if (error.response?.status === 401 && !isAuthCheck) {
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/api/auth/me`,
        { withCredentials: true }
      );
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (identifier, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { identifier, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, role: data.role };
    } catch (error) {
      // Distinguish "credential" errors (backend responded) from "backend unreachable"
      // errors (network / gateway 400/502/503 / non-JSON response). In the latter,
      // error.response?.data may be a raw string like "Application not found".
      const status = error.response?.status;
      const rawData = error.response?.data;
      const isCredentialFailure = status === 401 && rawData?.detail;

      let errorMsg;
      if (isCredentialFailure) {
        errorMsg = formatApiErrorDetail(rawData.detail);
      } else if (!error.response) {
        errorMsg = "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
      } else if (typeof rawData === "string" || (status >= 500 && status <= 599) || status === 502 || status === 503) {
        errorMsg = `Serviço temporariamente indisponível (${status}). Tente novamente em instantes ou contate o suporte.`;
      } else {
        errorMsg = formatApiErrorDetail(rawData?.detail) || error.message || "Algo deu errado. Tente novamente.";
      }
      return { success: false, error: errorMsg };
    }
  };

  const register = async ({ name, phone, password, email, role = 'patient' }) => {
    try {
      const payload = { name, phone, password, role };
      if (email) payload.email = email;
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;